/**
 * Dashboard Overview Page
 *
 * Clean, organized dashboard — no redundancy, clear hierarchy:
 * 1. Greeting header with plan badge
 * 2. 4 stat cards (compact, colored icons + progress)
 * 3. Chart + Usage breakdown (2-col)
 * 4. Agent list + Live activity (2-col)
 *
 * All data from CleverAct AI backend, 30s auto-refresh.
 */

import React from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Bot,
  MessageSquare,
  TrendingUp,
  Plus,
  UserCheck,
  Zap,
  HardDrive,
  UsersIcon,
  ArrowUpRight,
  ArrowDownRight,
  ArrowRight,
} from 'lucide-react'
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
} from 'recharts'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  CardAction,
} from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { Badge } from '../../components/ui/Badge'
import { Progress } from '../../components/ui/progress'
import { Skeleton } from '../../components/ui/Skeleton'
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '../../components/ui/chart'
import type { ChartConfig } from '../../components/ui/chart'

import { useChatbots } from '../../stores/chatbotStore'
import { useAuth } from '../../hooks/useAuth'
import { logger } from '../../utils/logger'
import {
  getUserUsage,
  formatUsageNumber,
  formatStorageSize,
  getUsageStatusColor,
  type UserAnalyticsResponse,
} from '../../services/userAnalytics'
import {
  getUserUsageV2,
  getBudgetStatus,
  type UserUsageV2,
  type BudgetStatus,
  formatLimit as formatLimitV2,
  formatDollar,
} from '../../services/userUsage'
import {
  analyticsService,
  type ConversationTrends,
} from '../../services/analytics'

// ====================================
// HELPERS
// ====================================

function getGreeting(): string {
  const hour = new Date().getHours()
  if (hour < 12) return 'Good morning'
  if (hour < 18) return 'Good afternoon'
  return 'Good evening'
}

const pct = (used: number, limit: number) =>
  limit > 0 ? Math.round((used / limit) * 100) : 0

// ====================================
// STAT CARD — SleepCard / ActiveCard pattern
// Clean: icon + label in header, big number, progress bar
// ====================================

function StatCard({
  title,
  value,
  unit,
  percentage,
  limit,
  icon: Icon,
  iconColor,
  progressColor,
  growthRate,
  isLoading,
}: {
  title: string
  value: string | number
  unit?: string
  percentage?: number
  limit?: string
  icon: React.ElementType
  iconColor: string
  progressColor: string
  growthRate?: number
  isLoading: boolean
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-muted-foreground text-sm font-medium">{title}</CardTitle>
          <Icon className={`h-4 w-4 ${iconColor}`} />
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {isLoading ? (
          <Skeleton className="h-9 w-20" />
        ) : (
          <div className="flex items-baseline gap-1">
            <span className="text-3xl font-bold">{value}</span>
            {unit && <span className="text-muted-foreground text-sm">{unit}</span>}
          </div>
        )}

        {!isLoading && growthRate !== undefined && growthRate !== 0 && (
          <div className="flex items-center gap-1 text-xs">
            {growthRate > 0 ? (
              <span className="flex items-center gap-0.5 text-emerald-500">
                <ArrowUpRight className="size-3" />+{Math.abs(growthRate)}%
              </span>
            ) : (
              <span className="flex items-center gap-0.5 text-red-400">
                <ArrowDownRight className="size-3" />{Math.abs(growthRate)}%
              </span>
            )}
            <span className="text-muted-foreground">vs last month</span>
          </div>
        )}

        {percentage !== undefined && (
          <div className="space-y-2">
            <div className="flex items-center justify-between gap-3">
              <Progress value={Math.min(percentage, 100)} className="h-2 flex-1" indicatorColor={progressColor} />
              <span className="text-muted-foreground text-xs">{percentage}%</span>
            </div>
            {limit && <p className="text-muted-foreground text-xs">of {limit}</p>}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

// ====================================
// USAGE BREAKDOWN — DailyActivityCard pattern
// Rows with colored icon bg + progress bars
// ====================================

interface UsageRow {
  icon: React.ElementType
  label: string
  current: string | number
  target: string
  percentage: number
  color: string
  bgColor: string
}

function UsageBreakdownCard({
  rows,
  isLoading,
}: {
  rows: UsageRow[]
  isLoading: boolean
}) {
  return (
    <Card className="h-full pb-0">
      <CardHeader>
        <CardTitle>Usage Breakdown</CardTitle>
        <CardDescription>Current billing period</CardDescription>
      </CardHeader>
      {isLoading ? (
        <CardContent className="space-y-4 pb-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="flex items-center gap-3">
              <Skeleton className="size-9 rounded-xl" />
              <div className="flex-1 space-y-1">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-2 w-full" />
              </div>
            </div>
          ))}
        </CardContent>
      ) : (
        <div className="divide-y border-t">
          {rows.map((row) => (
            <div key={row.label} className="p-4 px-6">
              <div className="mb-2 flex items-start justify-between">
                <div className="flex items-start gap-3">
                  <div className={`${row.bgColor} rounded-xl p-2`}>
                    <row.icon className="text-muted-foreground size-4" />
                  </div>
                  <div className="space-y-0.5">
                    <p className="text-sm font-medium">{row.label}</p>
                    <p className="text-muted-foreground text-xs">
                      {row.current} / {row.target}
                    </p>
                  </div>
                </div>
                <p className={`text-sm font-medium ${getUsageStatusColor(row.percentage)}`}>
                  {row.percentage}%
                </p>
              </div>
              <Progress value={Math.min(row.percentage, 100)} indicatorColor={row.color} />
            </div>
          ))}
        </div>
      )}
    </Card>
  )
}

// ====================================
// AGENT LIST — WorkoutsCard pattern
// ====================================

function AgentListCard({
  chatbots,
  isLoading,
  onCreate,
  onViewAll,
}: {
  chatbots: Array<{ id: string; name: string; is_active: boolean; bot_type?: string }>
  isLoading: boolean
  onCreate: () => void
  onViewAll: () => void
}) {
  const displayBots = chatbots.slice(0, 4)

  return (
    <Card>
      <CardHeader>
        <CardTitle>Your Agents</CardTitle>
        <CardDescription>
          {chatbots.length} agent{chatbots.length !== 1 ? 's' : ''} configured
        </CardDescription>
        <CardAction>
          <Button variant="ghost" size="sm" onClick={onCreate}>
            <Plus className="size-4" />
          </Button>
        </CardAction>
      </CardHeader>
      <CardContent className="space-y-3">
        {isLoading ? (
          [1, 2, 3].map((i) => (
            <div key={i} className="flex items-center gap-3 rounded-xl bg-muted/50 p-4">
              <Skeleton className="size-10 rounded-xl" />
              <div className="flex-1 space-y-1">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-3 w-20" />
              </div>
              <Skeleton className="h-6 w-16 rounded-full" />
            </div>
          ))
        ) : displayBots.length === 0 ? (
          <div className="rounded-xl bg-muted/50 p-8 text-center">
            <Bot className="mx-auto size-8 text-muted-foreground mb-2" />
            <p className="text-sm font-medium">No agents yet</p>
            <p className="text-xs text-muted-foreground mt-1">Create your first AI agent to get started</p>
            <Button size="sm" className="mt-4" onClick={onCreate}>
              <Plus className="mr-1 size-3" /> Create Agent
            </Button>
          </div>
        ) : (
          <>
            {displayBots.map((bot) => {
              const isActive = bot.is_active
              return (
                <div key={bot.id} className="bg-muted/50 flex items-center justify-between rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    <div className={`rounded-xl p-3 ${isActive ? 'bg-green-100 dark:bg-green-950' : 'bg-muted'}`}>
                      <Bot className={`size-4 ${isActive ? 'text-green-600 dark:text-green-400' : ''}`} />
                    </div>
                    <div className="space-y-0.5">
                      <p className="text-sm font-medium">{bot.name}</p>
                      <p className="text-muted-foreground text-xs">
                        {bot.bot_type === 'custom' ? 'Custom' : 'Support'} agent
                      </p>
                    </div>
                  </div>
                  <Badge
                    className={`rounded-full px-3 py-1 text-xs font-medium ${
                      isActive
                        ? 'bg-green-100 dark:bg-green-950 text-green-700 dark:text-green-200'
                        : 'bg-muted text-muted-foreground'
                    }`}
                  >
                    {isActive ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
              )
            })}
            {chatbots.length > 4 && (
              <Button variant="ghost" size="sm" className="w-full text-xs" onClick={onViewAll}>
                View all {chatbots.length} agents <ArrowRight className="ml-1 size-3" />
              </Button>
            )}
          </>
        )}
      </CardContent>
    </Card>
  )
}

// ====================================
// LIVE ACTIVITY — TrackingCard pattern
// ====================================

function LiveActivityCard() {
  const [metrics, setMetrics] = React.useState({
    conversations_today: 0,
    messages_today: 0,
    active_agents: 0,
  })

  React.useEffect(() => {
    const fetchMetrics = async () => {
      try {
        const data = await analyticsService.getMetricsSummary()
        if (data) {
          setMetrics({
            conversations_today: data.conversations_today || 0,
            messages_today: data.messages_today || 0,
            active_agents: data.active_agents || 0,
          })
        }
      } catch {
        /* best-effort */
      }
    }
    fetchMetrics()
    const interval = setInterval(fetchMetrics, 30000)
    return () => clearInterval(interval)
  }, [])

  const stats = [
    { label: 'Conversations', value: metrics.conversations_today },
    { label: 'Messages', value: metrics.messages_today },
    { label: 'Active Agents', value: metrics.active_agents },
  ]

  return (
    <Card className="h-full pb-0">
      <CardHeader>
        <CardTitle className="text-muted-foreground font-medium">Today</CardTitle>
        <CardAction>
          <div className="flex items-center gap-1.5 text-xs text-emerald-500">
            <span className="relative flex size-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex size-2 rounded-full bg-emerald-500" />
            </span>
            Live
          </div>
        </CardAction>
      </CardHeader>
      <CardContent className="border-t">
        <div className="grid grid-cols-3 divide-x">
          {stats.map((stat) => (
            <div key={stat.label} className="space-y-1 py-4 first:pl-0 pl-4">
              <p className="text-muted-foreground text-xs">{stat.label}</p>
              <p className="text-2xl font-bold tabular-nums">{stat.value}</p>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}

// ====================================
// CHART
// ====================================

const trendsChartConfig = {
  conversations: { label: 'Conversations', color: 'hsl(var(--chart-1))' },
  messages: { label: 'Messages', color: 'hsl(var(--chart-2))' },
} satisfies ChartConfig

function TrendsChart({
  data,
  isLoading,
}: {
  data: ConversationTrends[]
  isLoading: boolean
}) {
  return (
    <Card className="h-full">
      <CardHeader>
        <CardTitle>Conversation Trends</CardTitle>
        <CardDescription>Last 7 days</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-[240px] w-full" />
        ) : !data.length ? (
          <div className="flex h-[240px] items-center justify-center">
            <div className="text-center">
              <MessageSquare className="mx-auto size-8 text-muted-foreground mb-2" />
              <p className="text-sm text-muted-foreground">No conversations yet</p>
              <p className="text-xs text-muted-foreground mt-1">Trends will appear here as you get messages</p>
            </div>
          </div>
        ) : (
          <ChartContainer config={trendsChartConfig} className="h-[240px] w-full">
            <AreaChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="fillConv" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="var(--color-conversations)" stopOpacity={0.4} />
                  <stop offset="95%" stopColor="var(--color-conversations)" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="fillMsg" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="var(--color-messages)" stopOpacity={0.4} />
                  <stop offset="95%" stopColor="var(--color-messages)" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="date"
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                tick={{ fontSize: 11 }}
                tickFormatter={(val: string) =>
                  new Date(val).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                }
              />
              <YAxis tickLine={false} axisLine={false} tickMargin={8} width={32} tick={{ fontSize: 11 }} />
              <ChartTooltip
                content={
                  <ChartTooltipContent
                    labelFormatter={(val: string) =>
                      new Date(val).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                    }
                  />
                }
              />
              <Area dataKey="conversations" type="monotone" fill="url(#fillConv)" stroke="var(--color-conversations)" strokeWidth={2} />
              <Area dataKey="messages" type="monotone" fill="url(#fillMsg)" stroke="var(--color-messages)" strokeWidth={2} />
            </AreaChart>
          </ChartContainer>
        )}
      </CardContent>
    </Card>
  )
}

// ====================================
// MAIN
// ====================================

export const DashboardOverview: React.FC = () => {
  const navigate = useNavigate()
  const { user } = useAuth()


  const [usageData, setUsageData] = React.useState<UserAnalyticsResponse | null>(null)
  const [usageV2, setUsageV2] = React.useState<UserUsageV2 | null>(null)
  const [budget, setBudget] = React.useState<BudgetStatus | null>(null)
  const [isLoadingUsage, setIsLoadingUsage] = React.useState(true)
  const [usageError, setUsageError] = React.useState<string | null>(null)
  const [trendsData, setTrendsData] = React.useState<ConversationTrends[]>([])
  const [isLoadingTrends, setIsLoadingTrends] = React.useState(true)

  const { chatbots, isLoading, error, fetchChatbots, fetchDashboardStats } = useChatbots()

  React.useEffect(() => {
    logger.component('DashboardOverview', 'mount', {
      chatbotsCount: chatbots.length,
      isLoading,
      hasError: !!error,
    })
    fetchUsageData()
    const intervalId = setInterval(fetchUsageData, 30000)
    return () => clearInterval(intervalId)
  }, [])

  const fetchUsageData = async () => {
    setIsLoadingUsage(true)
    setIsLoadingTrends(true)
    setUsageError(null)
    try {
      const [legacy, v2, trends, budgetData] = await Promise.all([
        getUserUsage('30d').catch(() => null),
        getUserUsageV2().catch(() => null),
        analyticsService.getConversationTrends(7).catch(() => [] as ConversationTrends[]),
        getBudgetStatus().catch(() => null),
      ])
      if (legacy) setUsageData(legacy)
      if (v2) setUsageV2(v2)
      if (budgetData) setBudget(budgetData)
      setTrendsData(trends || [])
    } catch (err: unknown) {
      setUsageError(err instanceof Error ? err.message : 'Failed to load usage data')
      logger.error('Failed to load usage data', { error: err })
    } finally {
      setIsLoadingUsage(false)
      setIsLoadingTrends(false)
    }
  }

  // ── Computed ──
  const firstName = user?.full_name?.split(' ')[0] || 'there'

  const limitReached = !!(
    usageV2?.chatbots && usageV2.chatbots.limit !== -1 && usageV2.chatbots.used >= usageV2.chatbots.limit
  )

  const usageRows: UsageRow[] = React.useMemo(() => {
    if (!usageV2 && !usageData && !budget) return []
    return [
      {
        icon: TrendingUp,
        label: 'AI Credits',
        current: budget ? formatDollar(budget.ai_cost_usd) : '$0.00',
        target: budget && budget.ai_budget_usd > 0 ? formatDollar(budget.ai_budget_usd) : 'Unlimited',
        percentage: budget ? budget.budget_used_percent : 0,
        color: 'bg-emerald-500',
        bgColor: 'bg-emerald-100 dark:bg-emerald-950',
      },
      {
        icon: Bot,
        label: 'Agents',
        current: usageV2?.chatbots ? usageV2.chatbots.used : (usageData?.current_usage.chatbots || 0),
        target: usageV2?.chatbots ? formatLimitV2(usageV2.chatbots.limit) : String(usageData?.plan_limits.chatbots || 0),
        percentage: usageV2?.chatbots && usageV2.chatbots.limit > 0
          ? pct(usageV2.chatbots.used, usageV2.chatbots.limit)
          : (usageData?.usage_percentages.chatbots || 0),
        color: 'bg-violet-500',
        bgColor: 'bg-violet-100 dark:bg-violet-950',
      },
      {
        icon: UsersIcon,
        label: 'Teams',
        current: usageV2?.teams ? usageV2.teams.used : 0,
        target: usageV2?.teams ? formatLimitV2(usageV2.teams.limit) : '—',
        percentage: usageV2?.teams && usageV2.teams.limit > 0 ? pct(usageV2.teams.used, usageV2.teams.limit) : 0,
        color: 'bg-amber-500',
        bgColor: 'bg-amber-100 dark:bg-amber-950',
      },
      {
        icon: MessageSquare,
        label: 'Messages',
        current: usageV2?.messages ? usageV2.messages.used : (usageData?.current_usage.messages_count || 0),
        target: usageV2?.messages ? formatLimitV2(usageV2.messages.limit) : formatUsageNumber(usageData?.plan_limits.messages || 0),
        percentage: usageV2?.messages && usageV2.messages.limit > 0
          ? pct(usageV2.messages.used, usageV2.messages.limit)
          : (usageData?.usage_percentages.messages || 0),
        color: 'bg-blue-500',
        bgColor: 'bg-blue-100 dark:bg-blue-950',
      },
    ]
  }, [usageV2, usageData, budget])

  if (error) {
    logger.error('Dashboard error state', { component: 'DashboardOverview', error })
  }

  // ====================================
  // RENDER
  // ====================================

  return (
    <div className="space-y-6">
      {/* ── Header ── */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">
            {getGreeting()}, {firstName}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Here&rsquo;s your platform overview.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {usageV2?.plan_name && (
            <Badge variant="outline" className="capitalize text-xs">
              <Zap className="size-3 mr-1" />
              {usageV2.plan_name}
            </Badge>
          )}
          <Button onClick={() => navigate('/dashboard/agents/new')} disabled={limitReached}>
            <Plus className="mr-1.5 h-4 w-4" />
            New Agent
          </Button>
        </div>
      </div>

      {/* ── Banners ── */}
      {(usageV2 || usageData) &&
        (() => {
          const caps = usageV2
            ? [
                { name: 'team members', used: usageV2.team_members?.used || 0, limit: usageV2.team_members?.limit ?? -1 },
                { name: 'teams', used: usageV2.teams?.used || 0, limit: usageV2.teams?.limit ?? -1 },
                { name: 'chatbots', used: usageV2.chatbots?.used || 0, limit: usageV2.chatbots?.limit ?? -1 },
                { name: 'messages', used: usageV2.messages?.used || 0, limit: usageV2.messages?.limit ?? -1 },
              ]
            : [
                { name: 'messages', used: usageData?.current_usage.messages_count || 0, limit: usageData?.plan_limits.messages || -1 },
                { name: 'chatbots', used: usageData?.current_usage.chatbots || 0, limit: usageData?.plan_limits.chatbots || -1 },
              ]
          const worst = caps
            .filter((c) => typeof c.limit === 'number' && c.limit !== -1)
            .map((c) => ({ ...c, p: pct(c.used, c.limit) }))
            .sort((a, b) => b.p - a.p)[0]
          if (!worst || worst.p < 90) return null
          return (
            <div className="flex items-center justify-between rounded-lg border border-yellow-500/30 bg-yellow-500/5 px-4 py-3 text-sm">
              <span className="text-yellow-200">
                {worst.p >= 100
                  ? `You've reached your ${worst.name} limit (${worst.used}/${worst.limit}).`
                  : `${worst.p}% of your ${worst.name} limit used.`}
              </span>
              <Button variant="ghost" size="sm" className="text-yellow-200 hover:text-yellow-100" onClick={() => navigate('/dashboard/plans')}>
                Upgrade
              </Button>
            </div>
          )
        })()}

      {error && (
        <div className="flex items-center justify-between rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm">
          <div className="flex items-center gap-2">
            <div className="size-2 rounded-full bg-destructive" />
            <span className="text-destructive">{error}</span>
          </div>
          <Button variant="outline" size="sm" onClick={() => { fetchChatbots(1, 20, true); fetchDashboardStats() }}>
            Retry
          </Button>
        </div>
      )}

      {usageError && (
        <div className="flex items-center justify-between rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm">
          <div className="flex items-center gap-2">
            <div className="size-2 rounded-full bg-destructive" />
            <span className="text-destructive">{usageError}</span>
          </div>
          <Button variant="outline" size="sm" onClick={fetchUsageData}>Retry</Button>
        </div>
      )}

      {/* ── Row 1: 4 stat cards ── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Messages"
          value={usageData ? formatUsageNumber(usageData.current_usage.messages_count || usageData.current_usage.messages_used || 0) : '0'}
          percentage={
            usageData?.usage_percentages.messages !== undefined && usageData?.plan_limits.messages
              ? usageData.usage_percentages.messages : undefined
          }
          limit={usageData?.plan_limits.messages ? formatUsageNumber(usageData.plan_limits.messages) : undefined}
          icon={MessageSquare}
          iconColor="text-blue-500"
          progressColor="bg-blue-500"
          growthRate={usageData?.growth_rates.messages}
          isLoading={isLoadingUsage}
        />
        <StatCard
          title="Agents"
          value={usageV2?.chatbots ? usageV2.chatbots.used : (usageData?.current_usage.chatbots || 0)}
          percentage={
            usageV2?.chatbots && usageV2.chatbots.limit > 0 ? pct(usageV2.chatbots.used, usageV2.chatbots.limit) : undefined
          }
          limit={usageV2?.chatbots ? formatLimitV2(usageV2.chatbots.limit) : undefined}
          icon={Bot}
          iconColor="text-violet-500"
          progressColor="bg-violet-500"
          isLoading={isLoadingUsage}
        />
        <StatCard
          title="Team Members"
          value={usageV2?.team_members ? usageV2.team_members.used : (usageData?.current_usage.team_members || 0)}
          percentage={
            usageV2?.team_members && usageV2.team_members.limit > 0 ? pct(usageV2.team_members.used, usageV2.team_members.limit) : undefined
          }
          limit={usageV2?.team_members ? formatLimitV2(usageV2.team_members.limit) : undefined}
          icon={UserCheck}
          iconColor="text-amber-500"
          progressColor="bg-amber-500"
          isLoading={isLoadingUsage}
        />
        <StatCard
          title="AI Credits"
          value={budget ? formatDollar(budget.total_cost_usd) : '$0.00'}
          percentage={budget ? budget.budget_used_percent : undefined}
          limit={budget && budget.ai_budget_usd > 0 ? formatDollar(budget.ai_budget_usd) : 'unlimited'}
          icon={TrendingUp}
          iconColor="text-emerald-500"
          progressColor="bg-emerald-500"
          isLoading={isLoadingUsage}
        />
      </div>

      {/* ── Row 2: Chart (8 cols) + Usage breakdown (4 cols) ── */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <div className="lg:col-span-8">
          <TrendsChart data={trendsData} isLoading={isLoadingTrends} />
        </div>
        <div className="lg:col-span-4">
          <UsageBreakdownCard rows={usageRows} isLoading={isLoadingUsage} />
        </div>
      </div>

      {/* ── Row 3: Agent list (8 cols) + Live activity (4 cols) ── */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <div className="lg:col-span-8">
          <AgentListCard
            chatbots={chatbots}
            isLoading={isLoading}
            onCreate={() => navigate('/dashboard/agents/new')}
            onViewAll={() => navigate('/dashboard/chatbots')}
          />
        </div>
        <div className="lg:col-span-4">
          <LiveActivityCard />
        </div>
      </div>

    </div>
  )
}
